import { useCallback, useMemo } from "react";
import { GridPaginationModel } from "@mui/x-data-grid";
import {
	ConsumptionPageDTO,
	NodeDetailsDTO,
	ResultsBasePayload,
} from "../../../../../datasources/api-client/data-contracts";
import { ApiRoutes } from "../../../../../datasources/networking/apiRoutes";
import { usePeriodFilter } from "../../../../../hooks/usePreferences";
import { useReadResource } from "../../../../../hooks/useResource";

interface Options {
	payload: ResultsBasePayload;
	id: string;
	paginationModel: GridPaginationModel;
	isFiltersLoadingFromStorage?: boolean;
}

export function useOperationalUnitsData({ payload, id, paginationModel, isFiltersLoadingFromStorage }: Options) {
	const { formattedFrom: from, formattedTo: to, isPeriodReady } = usePeriodFilter();

	const isLoadingInitialState = isFiltersLoadingFromStorage || !isPeriodReady;

	const requestPayload = useMemo<ResultsBasePayload>(
		() => ({
			...payload,
			from,
			to,
		}),
		[payload, from, to]
	);

	// Node details
	const dataNode = useReadResource<undefined, NodeDetailsDTO>(
		{
			route: ApiRoutes.ResultsSitesNodeDetails,
			path: { id },
			query: {
				from,
				to,
			},
		},
		{
			disabled: !isPeriodReady,
		}
	);

	// Consumptions
	const electricity = useReadResource<ResultsBasePayload, ConsumptionPageDTO>(
		{
			route: ApiRoutes.ResultsNodeElectricityConsumption,
			query: {
				dataOrigin: "greenrouter",
				page: paginationModel.page,
				pageSize: paginationModel.pageSize,
				from,
				to,
			},
			path: { id },
		},
		{
			readMethod: "post",
			disabled: isLoadingInitialState,
		},
		requestPayload
	);

	const fuel = useReadResource<ResultsBasePayload, ConsumptionPageDTO>(
		{
			route: ApiRoutes.ResultsNodeFuelConsumption,
			query: {
				dataOrigin: "greenrouter",
				page: paginationModel.page,
				pageSize: paginationModel.pageSize,
				from,
				to,
			},
			path: { id },
		},
		{
			readMethod: "post",
			disabled: isLoadingInitialState,
		},
		requestPayload
	);

	const refrigerants = useReadResource<ResultsBasePayload, ConsumptionPageDTO>(
		{
			route: ApiRoutes.ResultsNodeRefrigerantsConsumption,
			query: {
				dataOrigin: "greenrouter",
				page: paginationModel.page,
				pageSize: paginationModel.pageSize,
				from,
				to,
			},
			path: { id },
		},
		{
			readMethod: "post",
			disabled: isLoadingInitialState,
		},
		requestPayload
	);

	const processGas = useReadResource<ResultsBasePayload, ConsumptionPageDTO>(
		{
			route: ApiRoutes.ResultsNodeProcessGasConsumption,
			query: {
				dataOrigin: "greenrouter",
				page: paginationModel.page,
				pageSize: paginationModel.pageSize,
				from,
				to,
			},
			path: { id },
		},
		{
			readMethod: "post",
			disabled: isLoadingInitialState,
		},
		requestPayload
	);

	const heatSteam = useReadResource<ResultsBasePayload, ConsumptionPageDTO>(
		{
			route: ApiRoutes.ResultsNodeHeatSteamConsumption,
			query: {
				dataOrigin: "greenrouter",
				page: paginationModel.page,
				pageSize: paginationModel.pageSize,
				from,
				to,
			},
			path: { id },
		},
		{
			readMethod: "post",
			disabled: isLoadingInitialState,
		},
		requestPayload
	);

	const water = useReadResource<ResultsBasePayload, ConsumptionPageDTO>(
		{
			route: ApiRoutes.ResultsNodeWaterConsumption,
			query: {
				dataOrigin: "greenrouter",
				page: paginationModel.page,
				pageSize: paginationModel.pageSize,
				from,
				to,
			},
			path: { id },
		},
		{
			readMethod: "post",
			disabled: isLoadingInitialState,
		},
		requestPayload
	);

	// Loading state
	const isLoadingConsumption =
		electricity.isLoading ||
		fuel.isLoading ||
		refrigerants.isLoading ||
		processGas.isLoading ||
		heatSteam.isLoading ||
		water.isLoading;

	const reloadConsumption = useCallback(() => {
		dataNode.reload();
		electricity.reload();
		fuel.reload();
		refrigerants.reload();
		processGas.reload();
		heatSteam.reload();
		water.reload();
	}, []);

	return {
		dataNode,
		isLoading: dataNode.isLoading,
		reload: reloadConsumption,
		consumption: {
			sources: {
				electricity: electricity.data,
				fuel: fuel.data,
				refrigerants: refrigerants.data,
				processGas: processGas.data,
				heatSteam: heatSteam.data,
				water: water.data,
			},
			isLoading: isLoadingConsumption,
			// all consumption calls belong to the same feature: one of them is enough
			isForbidden: electricity.isForbidden,
			reload: reloadConsumption,
		},
	};
}
